import { Link, useNavigate, type LinkProps } from '@tanstack/react-router';
import {
  Boxes,
  Briefcase,
  FileEdit,
  FileUp,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Settings,
  Shield,
  Sun,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useMemo, useState, type ElementType, type ReactNode } from 'react';

import { Button } from '@/components/button.js';
import { LanguageSwitcher } from '@/components/language-switcher.js';
import { Tooltip } from '@/components/tooltip.js';
import { useAuth } from '@/lib/auth-context.js';
import { cn } from '@/lib/cn.js';
import { useTheme } from '@/lib/theme.js';
import { useTranslation } from 'react-i18next';

interface NavItem {
  labelKey: string;
  to: LinkProps['to'];
  Icon: ElementType;
}

const BASE_NAV: NavItem[] = [
  { labelKey: 'nav.dashboard', to: '/', Icon: LayoutDashboard },
  { labelKey: 'nav.jobs', to: '/jobs', Icon: ListChecks },
  { labelKey: 'nav.templates', to: '/templates', Icon: FileEdit },
  { labelKey: 'nav.aiKeys', to: '/settings/ai-keys', Icon: KeyRound },
  { labelKey: 'nav.legacyImport', to: '/tools/import', Icon: FileUp },
  { labelKey: 'nav.settings', to: '/settings', Icon: Settings },
];

const ADMIN_NAV: NavItem[] = [
  { labelKey: 'nav.allJobs', to: '/admin/jobs', Icon: Briefcase },
  { labelKey: 'nav.sharedTokens', to: '/admin/shared-tokens', Icon: Boxes },
  { labelKey: 'nav.users', to: '/admin/users', Icon: Users },
  { labelKey: 'nav.proxy', to: '/admin/proxy', Icon: Shield },
  { labelKey: 'nav.costs', to: '/admin/costs', Icon: Wallet },
];

function Logo(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5">
      <img src="/favicon.svg" alt={t('auth.productName')} className="h-9 w-9 shrink-0" />
      <div className="leading-tight">
        <div className="text-[15px] font-bold tracking-tight">Article Gen</div>
        <div className="text-[11px] text-muted-foreground">{t('nav.jobs')}</div>
      </div>
    </div>
  );
}

function NavLink({ item }: { item: NavItem }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Link
      to={item.to}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        'text-sidebar-foreground hover:bg-accent hover:text-accent-foreground',
        '[&.active]:bg-primary/10 [&.active]:text-primary',
      )}
      activeProps={{ className: 'active' }}
    >
      <item.Icon
        size={18}
        className="text-muted-foreground group-hover:text-accent-foreground group-[.active]:text-primary transition-colors"
      />
      <span className="flex-1 text-left">{t(item.labelKey)}</span>
    </Link>
  );
}

function SidebarContent({ onClose }: { onClose?: () => void }): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const nav = useMemo<NavItem[]>(
    () => (auth.user?.role === 'ADMIN' ? [...BASE_NAV, ...ADMIN_NAV] : BASE_NAV),
    [auth.user?.role],
  );

  const onLogout = async () => {
    await auth.logout();
    void navigate({ to: '/login' });
  };

  const userInitial = auth.user?.email?.[0]?.toUpperCase() ?? 'U';

  return (
    <>
      <div className="flex items-center justify-between px-4 py-5">
        <Logo />
        {onClose && (
          <button
            onClick={onClose}
            aria-label={t('nav.closeMenu')}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent md:hidden"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        {nav.map((item) => (
          <NavLink key={String(item.to)} item={item} />
        ))}
        {auth.user?.role === 'ADMIN' && (
          <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t('nav.admin')}
          </div>
        )}
      </nav>

      <div className="border-t border-sidebar-border px-4 py-3 text-[11px] leading-relaxed text-muted-foreground">
        {t('nav.contact')}{' '}
        <span className="font-semibold text-foreground">verstak3000</span>
      </div>

      {auth.user && (
        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
              {userInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">{auth.user.email}</div>
              <div className="text-[11px] text-muted-foreground">
                {auth.user.role === 'ADMIN' ? t('roles.admin') : t('roles.user')}
              </div>
            </div>
            <Tooltip label={t('nav.signOut')}>
              <Button variant="ghost" size="iconSm" onClick={onLogout} aria-label={t('nav.signOut')}>
                <LogOut size={15} />
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[252px] flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-[270px] max-w-[82vw] flex-col border-r border-sidebar-border bg-sidebar shadow-lift animate-fade-up">
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col md:pl-[252px]">
        {/* Sticky header */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-5 lg:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openMenu')}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-accent md:hidden"
          >
            <Menu size={20} />
          </button>

          <div className="ml-auto flex items-center gap-2 sm:gap-2.5">
            <LanguageSwitcher />
            <Tooltip label={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}>
              <Button variant="outline" size="icon" onClick={toggle} aria-label={t('theme.toggle')}>
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </Button>
            </Tooltip>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-5 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-[1180px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
